"use client";

import { ArrowDown } from "lucide-react";
import * as React from "react";

import parse from "@/components/html-parser";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import UILink from "@/components/ui/link";
import { Scroll as ScrollSpy } from "@/components/ui/scroll";
import { Page } from "@/types";

interface OverviewProps {
	faqs?: any;
	summary_tree?: any;
	summary?: any;
	synonyms?: string[];
	not_synonyms?: string[];
	page: Page;
}

const Overview = ({ faqs, summary_tree, synonyms, not_synonyms, summary, page }: OverviewProps) => {
	const isSummaryTree = summary_tree?.length && !faqs?.length;
	const isSummary = summary?.length > 0 && !faqs?.length && !summary_tree?.length;
	const isFAQ = faqs?.length > 0;
	const isSynonymsOrNot = synonyms?.length > 0 || not_synonyms?.length > 0;
	const { type, slug } = page;

	return (
		<div id="summary" className="scroll-mt-20">
			{isSummaryTree
				? summary_tree?.map(
						(item: any) =>
							item?.content && (
								<Accordion
									type="single"
									collapsible
									key={item?.slug || item?.id}
									id={item?.slug || item?.id}
									defaultValue={item?.slug || item?.id}
								>
									<AccordionItem value={item?.slug || item?.id}>
										<AccordionTrigger
											copyLinkUrl={page?.isPaidAd ? "#0" : `${page?.url}/#${item.slug}`}
											as="h2"
											className="group flex items-center border-b border-dashed border-current pb-2 md:text-30 3xl:text-40"
										>
											<span>{parse(item?.text)}</span>
										</AccordionTrigger>

										<AccordionContent>
											<div className="content">{parse(item?.content)}</div>
										</AccordionContent>
									</AccordionItem>
								</Accordion>
							)
				  )
				: null}

			{isSummary ? (
				<Accordion type="single" collapsible id="summary" defaultValue="summary">
					<AccordionItem value="summary">
						<AccordionTrigger
							copyLinkUrl={page?.isPaidAd ? "#0" : `${page?.url}/#summary`}
							as="h2"
							className="group flex items-center border-b border-dashed border-current pb-2 md:text-30 3xl:text-40"
						>
							<span>Summary</span>
						</AccordionTrigger>

						<AccordionContent>
							<div className="content">{parse(summary)}</div>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			) : null}

			{isFAQ
				? faqs?.map((item: any) => (
						<Accordion type="single" key={item?.slug} defaultValue={item?.slug} collapsible>
							<AccordionItem id={item?.slug} value={item?.slug}>
								<AccordionTrigger
									copyLinkUrl={page?.isPaidAd ? "#0" : `${page?.url}/#${item.slug}`}
									as="h2"
									className="group flex items-center border-b border-dashed border-current pb-2 md:text-30 3xl:text-40"
								>
									<span>{parse(item?.title)}</span>
								</AccordionTrigger>

								<AccordionContent>
									<div className="content">{parse(item?.quick_summary)}</div>

									<div className="flex flex-col space-y-5">
										{item?.children?.map((subFaq: any) => (
											<React.Fragment key={subFaq?.slug}>
												{page.isSplitTest ? (
													<UILink
														href={`/${type}/${slug}/faq/#${subFaq.slug}`}
														className="group mt-3 inline-flex cursor-pointer items-start space-x-2 -tracking-2 lg:space-x-3 lg:text-lg lg:hover:underline xl:text-xl"
													>
														<span className="my-1 grid size-4 shrink-0 place-content-center rounded-full border border-primary transition-colors duration-300 lg:size-5 lg:group-hover:bg-primary lg:group-hover:text-white">
															<ArrowDown className="size-3 xl:size-3.5" strokeWidth="1.7" />
														</span>
														<span>{parse(subFaq?.title)}</span>
													</UILink>
												) : (
													<ScrollSpy
														to={subFaq?.slug}
														spy={true}
														offset={-90}
														smooth={true}
														hashSpy={true}
														duration={300}
														activeClass="isActive-link"
														className="group mt-3 inline-flex cursor-pointer items-start space-x-2 -tracking-2 lg:space-x-3 lg:text-lg lg:hover:underline xl:text-xl"
													>
														<span className="my-1 grid size-4 shrink-0 place-content-center rounded-full border border-primary transition-colors duration-300 lg:size-5 lg:group-hover:bg-primary lg:group-hover:text-white">
															<ArrowDown className="size-3 xl:size-3.5" strokeWidth="1.7" />
														</span>
														<span>{parse(subFaq?.title)}</span>
													</ScrollSpy>
												)}
											</React.Fragment>
										))}
									</div>
								</AccordionContent>
							</AccordionItem>
						</Accordion>
				  ))
				: null}

			{isSynonymsOrNot ? (
				<Accordion id="also-known-as" type="single" defaultValue="also-known-as" collapsible>
					<AccordionItem id="also-known-as" value="also-known-as">
						<AccordionTrigger
							copyLinkUrl={page?.isPaidAd ? "#0" : `${page?.url}/#also-known-as`}
							as="h2"
							className="group flex items-center border-b border-dashed border-current pb-2 md:text-30 3xl:text-40"
						>
							<span className="flex items-center space-x-4">
								What are other names for {page?.name}?
							</span>
						</AccordionTrigger>

						<AccordionContent>
							<div className="content">
								{synonyms?.length > 0 && (
									<div>
										<p>Note that {page?.name} is also known as:</p>{" "}
										<ul>
											{synonyms?.map((item: string) => (
												<li key={item} className="px-1">
													{parse(item)}
												</li>
											))}
										</ul>
									</div>
								)}
								{not_synonyms?.length > 0 && (
									<div>
										<p>{page?.name} should not be confused with:</p>{" "}
										<ul>
											{not_synonyms?.map((item: string) => (
												<li key={item} className="px-1 ">
													{parse(item)}
												</li>
											))}
										</ul>
									</div>
								)}
							</div>
						</AccordionContent>
					</AccordionItem>
				</Accordion>
			) : null}
		</div>
	);
};

export { Overview };
